// @ts-check
/**
 * FF Related Products - Custom element for dynamic product recommendations.
 *
 * Uses Shopify's Product Recommendations API with Section Rendering:
 * GET /recommendations/products?section_id=ff-product-recommendations&product_id={id}&limit={n}&intent={intent}
 *
 * Features:
 * - Fetches product recommendations on load (supports 'related' and 'complementary' intents)
 * - Replaces placeholder slides with real recommendation cards
 * - Integrates with ff-slider (Splide.js)
 * - Auto-hides when no recommendations are available
 */

if (!customElements.get('ff-related-products')) {
  class FfRelatedProducts extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement | null} */
      this.ffSlider = null;

      /** @type {object | null} */
      this.splideInstance = null;
    }

    connectedCallback() {
      this.ffSlider = this.querySelector('ff-slider');
      this.initWhenReady();
    }

    initWhenReady() {
      // @ts-ignore - splide is attached by ff-slider custom element
      if (this.ffSlider && this.ffSlider.splide) {
        // @ts-ignore
        this.splideInstance = this.ffSlider.splide;
        this.loadRecommendations();
      } else {
        setTimeout(() => this.initWhenReady(), 100);
      }
    }

    async loadRecommendations() {
      this.setAttribute('data-loading', '');
      const productId = this.dataset.productId;
      const sectionId = this.dataset.sectionId || 'ff-product-recommendations';
      const limit = this.dataset.limit || '8';
      const intent = this.dataset.intent || 'related';

      if (!productId) {
        this.hide();
        return;
      }

      try {
        const params = new URLSearchParams({ section_id: sectionId, product_id: productId, limit, intent });
        const url = `${window.Shopify.routes.root}recommendations/products?${params}`;
        const response = await fetch(url);

        if (!response.ok) {
          console.warn('FfRelatedProducts: fetch failed', response.status);
          this.hide();
          return;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Inject card styles from the recommendations section (once per page)
        if (!document.head.querySelector('[data-ff-rec-styles]')) {
          doc.querySelectorAll('style').forEach((style) => {
            const clone = /** @type {HTMLStyleElement} */ (style.cloneNode(true));
            clone.dataset.ffRecStyles = '';
            document.head.appendChild(clone);
          });
        }

        const slides = doc.querySelectorAll('[data-ff-rec-slide]');

        if (slides.length === 0) {
          this.hide();
          return;
        }

        this.renderSlides(slides);
      } catch (e) {
        console.warn('FfRelatedProducts: error', e);
        this.hide();
      }
    }

    /**
     * @param {NodeListOf<Element>} slides
     */
    renderSlides(slides) {
      if (!this.ffSlider || !this.splideInstance) return;

      const list = this.ffSlider.querySelector('.splide__list');
      if (!list) return;

      list.innerHTML = '';
      slides.forEach((slide) => list.appendChild(slide.cloneNode(true)));

      this.splideInstance.go(0);
      this.snapAutoSlideWidth();
      this.splideInstance.refresh();

      // Re-check if arrows are needed after replacing slides
      requestAnimationFrame(() => {
        if (this.ffSlider && this.ffSlider.setupAutoWidthPagesCheck) {
          this.ffSlider.setupAutoWidthPagesCheck();
        }
      });

      this.removeAttribute('data-loading');
      this.show();
    }

    /**
     * Snaps slide width to the nearest N.5 visible count to always show a half-slide at the edge.
     * Only runs in autoWidth mode. Slides are made slightly wider (never squeezed).
     *
     * Examples: 2.7 visible → 2.5, 3.2 visible → 2.5, 4.0 visible → 3.5
     */
    snapAutoSlideWidth() {
      if (!this.ffSlider || !this.splideInstance) return;
      if (!this.splideInstance.options.autoWidth) return;

      const track = this.ffSlider.querySelector('.splide__track');
      const firstSlide = /** @type {HTMLElement | null} */ (
        this.ffSlider.querySelector('.splide__slide:not(.splide__slide--clone)')
      );
      if (!track || !firstSlide) return;

      const trackWidth = /** @type {HTMLElement} */ (track).clientWidth;
      const slideWidth = firstSlide.offsetWidth;
      const gapRaw = this.splideInstance.options.gap;
      const gap = typeof gapRaw === 'number' ? gapRaw : parseFloat(String(gapRaw)) || 0;

      if (slideWidth === 0 || trackWidth === 0) return;

      const fraction = parseFloat(this.dataset.ffSnapFraction || '');
      if (!fraction) return;

      const slideCount = this.ffSlider.querySelectorAll('.splide__slide:not(.splide__slide--clone)').length;
      const naturalVisible = (trackWidth + gap) / (slideWidth + gap);

      // All slides fit without overflow — nothing to snap
      if (naturalVisible >= slideCount) return;

      const snapped = Math.floor(naturalVisible - fraction) + fraction;

      // Already snapped or only one slide — nothing to do
      if (snapped === naturalVisible || snapped < 1) return;

      // (snapped) * newWidth + (snapped - fraction) * gap = trackWidth
      const newWidth = (trackWidth - (snapped - fraction) * gap) / snapped;

      const allSlides = this.ffSlider.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
      allSlides.forEach((/** @type {Element} */ s) => {
        /** @type {HTMLElement} */ (s).style.width = `${newWidth}px`;
      });
    }

    hide() {
      this.removeAttribute('data-loading');
      this.classList.add('ff-d-none');
    }

    show() {
      this.classList.remove('ff-d-none');
    }
  }

  customElements.define('ff-related-products', FfRelatedProducts);
}
